import { MoreHorizontal, Plus, Zap } from "lucide-react"
import { useState } from "react"
import type { DraftFlow } from "@/entities/bot-builder"
import type { DraftActions } from "@/features/edit-scenario-draft"
import { FlowTriggersDialog } from "@/features/edit-scenario-draft"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/ui/dialog"
import { Input } from "@/shared/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip"

interface FlowsBarProps {
	flows: DraftFlow[]
	activeFlowId: string
	errorsByFlow: Map<string, number>
	onSelect: (flowId: string) => void
	actions: Pick<DraftActions, "createFlow" | "renameFlow" | "deleteFlow" | "setFlowTriggers">
}

/** Потоки сценария вкладками: основной, «Не понял» и свои, в которые попадают через «Перейти». */
export function FlowsBar({ flows, activeFlowId, errorsByFlow, onSelect, actions }: FlowsBarProps) {
	const { confirm } = useConfirm()
	const [dialog, setDialog] = useState<{ flow?: DraftFlow; name: string } | null>(null)
	const [triggersFlow, setTriggersFlow] = useState<DraftFlow | null>(null)

	const submit = async () => {
		if (!dialog?.name.trim()) return
		if (dialog.flow) {
			await actions.renameFlow(dialog.flow.id, dialog.name.trim())
		} else {
			const result = await actions.createFlow(dialog.name.trim())
			if (result) onSelect(result.flow.id)
		}
		setDialog(null)
	}

	return (
		<div
			role="tablist"
			aria-label="Воронки сценария"
			className="flex max-w-full min-w-0 items-center gap-0.5 overflow-x-auto rounded-xl bg-muted/60 p-1"
		>
			{flows.map((flow) => {
				const active = flow.id === activeFlowId
				const errors = errorsByFlow.get(flow.id) ?? 0
				const triggerCount =
					(flow.triggers?.commands.length ?? 0) +
					(flow.triggers?.keywords.length ?? 0) +
					(flow.triggers?.events?.length ?? 0)
				const background = flow.triggers?.background === true
				return (
					<div
						key={flow.id}
						className={cn(
							"flex h-8 shrink-0 items-center rounded-lg text-sm transition-colors",
							active
								? "bg-background text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						<Button
							type="button"
							variant="ghost"
							role="tab"
							aria-selected={active}
							className="h-full rounded-none px-3 py-0 hover:bg-transparent"
							onClick={() => onSelect(flow.id)}
						>
							{flow.name}
							{errors > 0 ? (
								<span className="ml-1.5 rounded-full bg-red-500/20 px-1.5 text-[10px] leading-4 font-semibold text-red-300">
									{errors}
								</span>
							) : null}
						</Button>
						{flow.kind !== "FALLBACK" && (active || triggerCount > 0 || background)
							? (() => {
									const triggerHint =
										triggerCount > 0 || background
											? [
													...(background ? ["в фоне"] : []),
													...(flow.triggers?.commands.map((item) => `/${item.command}`) ?? []),
													...(flow.triggers?.keywords.map((item) => `«${item.text}»`) ?? []),
													...(flow.triggers?.events?.map((item) => `событие ${item.code}`) ?? []),
												].join(", ")
											: "Команды, ключевые слова и события для запуска"
									return (
										<Tooltip>
											<TooltipTrigger asChild>
												<Button
													type="button"
													variant="ghost"
													aria-label={`Запуск воронки ${flow.name}`}
													title={triggerHint}
													className={cn(
														"-ml-1 h-full gap-0.5 rounded-none py-0 pr-2.5 pl-2 hover:bg-transparent",
														triggerCount > 0
															? "text-amber-300"
															: "text-muted-foreground hover:text-foreground",
													)}
													onClick={() => setTriggersFlow(flow)}
												>
													<Zap className="h-3.5 w-3.5" />
													{triggerCount > 0 ? (
														<span className="text-[10px]">{triggerCount}</span>
													) : null}
													{background ? <span className="text-[10px]">фон</span> : null}
												</Button>
											</TooltipTrigger>
											<TooltipContent>{triggerHint}</TooltipContent>
										</Tooltip>
									)
								})()
							: null}
						{flow.kind === "CUSTOM" && active ? (
							<Popover>
								<PopoverTrigger asChild>
									<Button
										type="button"
										variant="ghost"
										size="icon"
										aria-label={`Действия с воронкой ${flow.name}`}
										className="-ml-1 h-full w-auto rounded-none pr-2 pl-2 text-muted-foreground hover:bg-transparent hover:text-foreground"
									>
										<MoreHorizontal className="h-4 w-4" />
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-44 rounded-xl p-1">
									<Button
										type="button"
										variant="ghost"
										className="h-auto w-full justify-start rounded-lg px-2.5 py-2 text-left text-sm"
										onClick={() => setDialog({ flow, name: flow.name })}
									>
										Переименовать
									</Button>
									<Button
										type="button"
										variant="ghost"
										className="h-auto w-full justify-start rounded-lg px-2.5 py-2 text-left text-sm text-red-400 hover:text-red-400"
										onClick={() =>
											confirm({
												title: `Удалить воронку «${flow.name}»?`,
												message: "Все его блоки удалятся.",
												confirmText: "Удалить",
												onConfirm: async () => {
													await actions.deleteFlow(flow.id)
												},
											})
										}
									>
										Удалить
									</Button>
								</PopoverContent>
							</Popover>
						) : null}
					</div>
				)
			})}
			<Button
				type="button"
				variant="ghost"
				size="icon"
				aria-label="Новая воронка"
				title="Новая воронка"
				className="h-8 w-8 shrink-0 rounded-lg text-muted-foreground hover:bg-background/60 hover:text-foreground"
				onClick={() => setDialog({ name: "" })}
			>
				<Plus className="h-4 w-4" />
			</Button>

			<FlowTriggersDialog
				flow={triggersFlow}
				onOpenChange={(open) => !open && setTriggersFlow(null)}
				onSave={actions.setFlowTriggers}
			/>
			<Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
				<DialogContent className="sm:max-w-sm">
					<DialogHeader>
						<DialogTitle>{dialog?.flow ? "Переименовать воронку" : "Новая воронка"}</DialogTitle>
					</DialogHeader>
					<form
						onSubmit={(event) => {
							event.preventDefault()
							void submit()
						}}
						className="space-y-4"
					>
						<Input
							autoFocus
							aria-label="Название воронки"
							placeholder="Например, Оплата"
							maxLength={64}
							value={dialog?.name ?? ""}
							onChange={(event) =>
								setDialog((current) => current && { ...current, name: event.target.value })
							}
						/>
						<DialogFooter>
							<Button type="submit" disabled={!dialog?.name.trim()}>
								{dialog?.flow ? "Сохранить" : "Создать"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	)
}
